import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper } from '@bimser/core';
import dayjs from 'dayjs';
import { formulaEvaluate } from '.';
import { IControlProperties, IGetControlPropertyValue } from '../../../entities';
import { IVariableItem } from '../../../entities/common';
import { IControlDataGridColumn } from '../../../environments/runtime/components/DataGrid/entities';
import _get from 'lodash/get';

export function getControlPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, data } = args;
	const control = data.get(controlId);
	if (!control) {
		return null;
	}
	let propertyValue;

	switch (control.type) {
		case FormItemTypes.EContainerTypes.Form: {
			propertyValue = getFormPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.ComboBox:
		case FormItemTypes.EControlTypes.Dropdown:
		case FormItemTypes.EControlTypes.RadioList:
		case FormItemTypes.EControlTypes.AutoComplete: {
			propertyValue = getAutoCompletePropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.TreeView:
		case FormItemTypes.EControlTypes.TreeSelect: {
			propertyValue = getTreeSelectPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.ListBox:
		case FormItemTypes.EControlTypes.TagBox:
		case FormItemTypes.EControlTypes.Transfer: {
			propertyValue = getTransferPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.DateTimePicker: {
			propertyValue = getDateTimePickerPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.DateRangePicker: {
			propertyValue = getDateRangePickerPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.TimePicker: {
			propertyValue = getTimePickerPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.HTMLTextBox:
		case FormItemTypes.EControlTypes.TextBox:
		case FormItemTypes.EControlTypes.ProtectedTextBox:
		case FormItemTypes.EControlTypes.TextArea: {
			propertyValue = getTextAreaPropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.Rate: {
			propertyValue = getRatePropertyValue(args);
			break;
		}
		case FormItemTypes.EControlTypes.DataGrid: {
			propertyValue = getDataGridPropertyValue(args);
			break;
		}
		case FormItemTypes.EContainerTypes.Section:
		case FormItemTypes.EContainerTypes.Column: {
			propertyValue = getLayoutItemPropertyValue(args);
			break;
		}
	}

	if (propertyValue === undefined) {
		propertyValue = getPropertyValue(args);
	}

	return propertyValue;
}

function getLayoutItemPropertyValue(args: IGetControlPropertyValue) {
	if (args.keys.includes('layout')) {
		const { controlId, keys, data } = args;
		const control = data.get(controlId);
		const dataGridPath = keys.split('.');
		return control.getIn(['designerProps', ...dataGridPath]);
	}
}

function getDataGridPropertyValue(args: IGetControlPropertyValue) {
	if (args.keys.includes('columns')) {
		const { controlId, keys, data } = args;
		const control = data.get(controlId);
		const dataGridPath = keys.split('.');
		const columns: Array<IControlDataGridColumn<IControlProperties>> = control.getIn([
			'properties',
			'columns'
		]);
		const column = columns.find(col => col.name === dataGridPath[1]);

		return _get(column, dataGridPath.splice(2).toString().replace(',', '.'));
	} else if (args.keys.includes('selectedRows.length')) {
		const { controlId, data } = args;
		const control = data.get(controlId);
		const rows = control.getIn(['properties', 'rows']);
		return rows.filter(row => row.selected).length;
	}
}

function getFormPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);
	const keysArr = keys.split('.');

	if (keysArr[0] === 'variables') {
		const variable: IVariableItem = control.getIn(['properties', ...keysArr]);

		if (variable) {
			return formulaEvaluate(variable.value, data);
		}
	}
}

function getAutoCompletePropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);
	const items: any[] = control.getIn(['properties', 'items']);
	const selectedItem = items.find(i => i.selected);

	if (keys === 'value' || keys === 'selectedItem.value') {
		return selectedItem?.value || null;
	}

	if (keys === 'text' || keys === 'selectedItem.text') {
		const text = selectedItem ? MLHelper.getMLText(selectedItem.text) : null;
		return text === '' ? null : text;
	}

	if (keys.includes('selectedItem.field')) {
		const dataSourceData: any[] = control.getIn(['properties', 'data']);
		const valueExpression = control.getIn(['properties', 'dataSource'])?.valueExpr;
		const selectedDataField =
			selectedItem &&
			dataSourceData?.find(
				item => item[valueExpression]?.toString() === selectedItem.value?.toString()
			);
		return selectedDataField ? selectedDataField[keys.split('.').pop()] : null;
	}
}

function getTreeSelectPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'value' || keys === 'selectedItem.value') {
		const selectedValues: any[] = control.getIn(['properties', 'value']);
		const valueSeparator: string = control.getIn(['properties', 'valueSeparator']);

		return selectedValues?.join(valueSeparator || ',') || null;
	}

	if (keys === 'text' || keys === 'selectedItem.text') {
		const selectedValues: any[] = control.getIn(['properties', 'value']);
		const items: any[] = control.getIn(['properties', 'treeItems']);
		const selectedItems: any[] = [];
		const getSelectedItems = (si: any[]) => {
			si.forEach((i: any) => {
				if (selectedValues.includes(i.key)) {
					selectedItems.push(i);
				}
				getSelectedItems(i.children);
			});
		};
		getSelectedItems(items);

		if (selectedItems) {
			const textSeparator: string = control.getIn(['properties', 'textSeparator']);
			const selecteValuesText: string[] = selectedItems.map(x => MLHelper.getMLText(x.text));

			return selecteValuesText?.join(textSeparator || ',') || null;
		}
	}
}

function getTransferPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'value' || keys === 'text') {
		const items: any[] = control.getIn(['properties', 'items']);
		const valueSeparator: string = control.getIn(['properties', 'valueSeparator']);
		const selectedItems = items.filter(i => i.selected);

		return selectedItems && selectedItems.length
			? selectedItems.map(i => i.value).join(valueSeparator || ',')
			: null;
	}
}

function getDateTimePickerPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'value' || keys === 'text') {
		const value = control.getIn(['properties', keys]);

		return value ? dayjs(value).startOf('day').valueOf() : null;
	}
}

function getDateRangePickerPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'startDate.value' || keys === 'endDate.value') {
		const value = control.getIn(['properties', ...keys.split('.')]);
		return value ? dayjs(value).startOf('day').valueOf() : null;
	}
}

function getTimePickerPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'value' || keys === 'text') {
		const value = control.getIn(['properties', keys]);
		const format = control.getIn(['properties', 'format']);
		let v = dayjs(value)
			.set('year', 1993)
			.set('month', 10)
			.set('date', 10)
			.set('millisecond', 10);

		if (format && format === 'HH:mm') {
			v = v.set('second', 0);
		}

		return value ? v.unix() * 1000 : null;
	}
}

function getTextAreaPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'value' || keys === 'text') {
		const multiLanguageText: IDictionary<string> = control.getIn([
			'properties',
			'multiLanguageText'
		]);
		const enableMultiLanguageText: boolean = control.getIn([
			'properties',
			'enableMultiLanguageText'
		]);

		if (!enableMultiLanguageText) {
			if (multiLanguageText) {
				return multiLanguageText['system'];
			}
			return '';
		}

		let d = multiLanguageText[MLHelper.currentLanguage.culture];

		if (isNullOrUndefined(d)) {
			d = multiLanguageText[MLHelper.defaultLanguage.culture];
		}

		if (isNullOrUndefined(d)) {
			const multiLanguageTextKeys = Object.keys(multiLanguageText);
			if (multiLanguageTextKeys.length > 0) {
				d = multiLanguageText[multiLanguageTextKeys[0]];
			}
		}

		return d || '';
	}
}

function getRatePropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data } = args;
	const control = data.get(controlId);

	if (keys === 'value') {
		const value = control.getIn(['properties', 'value']);
		if (!isNullOrUndefined(value) && value === 0) {
			return null;
		}
	}
}

function getPropertyValue(args: IGetControlPropertyValue) {
	const { controlId, keys, data, checkType, FormRuntimeItemManager } = args;
	const control = data.get(controlId);
	const keysArr = keys.split('.');
	const controlType = control.properties.get('controlType');
	const returnValue = control.getIn(['properties', ...keysArr]);

	if (checkType === 'preview' && returnValue === undefined) {
		const controlIns = FormRuntimeItemManager.getFormItemInstance(controlType);
		return _get(controlIns, keysArr);
	}

	return returnValue;
}
